// ---------------------------------------------------------------------------
// Front-desk guides - the four screens a host actually lives in.
//
// This lane is different from the setup lane in one important way: the person
// running it is not configuring anything, they are working a shift. So the copy
// answers "someone is standing in front of me, what do I press" first, and only
// then explains why the system is shaped the way it is.
//
// Each guide's permission names both the view code that opens the page and the
// manage code for the control it asks for, so nobody lands on a 403 or on a
// button that was never rendered for them.
// ---------------------------------------------------------------------------

import { PATH_ADMIN } from '../../routes/paths';
import { ANCHOR, CENTER } from '../anchors';
import { COACH_EVENT } from '../events';

const CHECKINS = PATH_ADMIN.checkIns;
const VISITORS = PATH_ADMIN.visitorManagement;
const MEETING_ROOMS = PATH_ADMIN.meetingRoomManagement.root;
const SR_HOME = PATH_ADMIN.serviceRequests.home;
const SR_CREATE = PATH_ADMIN.serviceRequests.create;
const SR_TICKETS = PATH_ADMIN.serviceRequests.tickets;

export const FRONTDESK_CHECKIN = {
  id: 'frontdesk-checkin',
  title: 'Check people in',
  summary:
    'Who is physically in the building today, where those records come from, '
    + 'and how to check someone in by hand when the usual route fails.',
  category: 'frontdesk',
  level: 'advanced',
  permission: ['checkins.view', 'checkins.manage'],
  featureSlug: null,
  estMinutes: 4,
  readinessKey: null,
  keywords: [
    'check in', 'checkin', 'check-in', 'attendance', 'who is in', 'kiosk',
    'day pass', 'manual check in', 'check out',
  ],
  steps: [
    {
      id: 'intro',
      kind: 'narrate',
      route: CHECKINS,
      target: CENTER,
      placement: 'center',
      title: 'This page answers one question: who is here right now',
      body:
        'A check-in is a person physically arriving. It is not a booking and it '
        + 'is not a membership. Someone can hold a desk all year and never check '
        + 'in once, and the seat still counts as sold. Keep the two apart in your '
        + 'head and this whole section stops being confusing.',
    },
    {
      id: 'sources',
      kind: 'narrate',
      route: CHECKINS,
      target: CENTER,
      placement: 'center',
      title: 'Most check-ins arrive without you',
      body:
        'The kiosk at the door, the member app, and the access reader all write '
        + 'to this same list. That is the point of it. You only step in when the '
        + 'automatic route fails: a flat phone, a guest on a day pass who has '
        + 'never used the kiosk, or someone the reader will not recognise.',
    },
    {
      id: 'manual',
      kind: 'act',
      route: CHECKINS,
      target: ANCHOR.checkinManual,
      placement: 'bottom',
      title: 'Check someone in by hand',
      body:
        'This is the fallback, and you will use it more in your first month than '
        + 'you expect to. Search for the member, confirm it is the right person, '
        + 'and record them in.',
      waitingLabel: 'Click Manual Check-in.',
      advanceOn: [{ type: 'click', target: ANCHOR.checkinManual }],
      doItForMe: { type: 'click', target: ANCHOR.checkinManual },
      allowSkip: true,
    },
    {
      id: 'confirm',
      kind: 'await',
      target: CENTER,
      placement: 'center',
      title: 'Pick the person and confirm',
      body:
        'If they are not in the list, they are not a member of this campus yet. '
        + 'That is a membership question rather than a door question, so send it '
        + 'to whoever handles sign-ups instead of forcing it here.',
      waitingLabel: 'Waiting for the check-in to be recorded.',
      advanceOn: [{ type: 'event', name: COACH_EVENT.memberCheckedIn }],
      allowSkip: true,
    },
    {
      id: 'checkout',
      kind: 'narrate',
      route: CHECKINS,
      target: CENTER,
      placement: 'center',
      title: 'Checking out matters more than it looks',
      body:
        'An open check-in means the system believes that person is still in the '
        + 'building. That is what a fire roll call reads from, so a day of stale '
        + 'open rows is a real problem rather than an untidy one. Close them out '
        + 'at the end of the day if people forget, and check with your manager '
        + 'whether your campus auto-closes them overnight.',
    },
  ],
};

export const FRONTDESK_VISITORS = {
  id: 'frontdesk-visitors',
  title: 'Handle visitors',
  summary:
    'Pre-invite a guest so they walk straight in, and handle the ones who turn '
    + 'up unannounced.',
  category: 'frontdesk',
  level: 'advanced',
  permission: ['visitors.view', 'visitors.manage'],
  featureSlug: null,
  estMinutes: 5,
  readinessKey: null,
  keywords: [
    'visitor', 'guest', 'invite', 'invitation', 'walk in', 'reception',
    'visitor pass', 'front desk', 'sign in',
  ],
  steps: [
    {
      id: 'intro',
      kind: 'narrate',
      route: VISITORS,
      target: CENTER,
      placement: 'center',
      title: 'A visitor is anyone without a membership',
      body:
        'A client coming to a meeting, a candidate for an interview, a courier. '
        + 'They get a record for the day, not an account. Two things live here: '
        + 'Visitors is everyone who has actually arrived, and Invitations is '
        + 'everyone who is expected.',
    },
    {
      id: 'invitations-tab',
      kind: 'act',
      route: VISITORS,
      target: ANCHOR.visitorsInvitationsTab,
      placement: 'bottom',
      title: 'Expected guests live here',
      body:
        'An invitation is the good path. The guest gets a pass before they '
        + 'arrive, scans it at the door, and nobody has to type anything while '
        + 'they stand there waiting.',
      waitingLabel: 'Click the Invitations tab.',
      advanceOn: [{ type: 'click', target: ANCHOR.visitorsInvitationsTab }],
      doItForMe: { type: 'click', target: ANCHOR.visitorsInvitationsTab },
      allowSkip: true,
    },
    {
      id: 'new-invite',
      kind: 'act',
      route: VISITORS,
      target: ANCHOR.visitorInviteNew,
      placement: 'bottom',
      title: 'Invite someone',
      body:
        'Members can raise these themselves from the portal, and most of what '
        + 'you see here will be theirs. You raise one when a member phones the '
        + 'desk instead.',
      waitingLabel: 'Click New invitation.',
      advanceOn: [{ type: 'click', target: ANCHOR.visitorInviteNew }],
      doItForMe: { type: 'click', target: ANCHOR.visitorInviteNew },
      allowSkip: true,
    },
    {
      id: 'fill-it',
      kind: 'await',
      target: CENTER,
      placement: 'center',
      title: 'Who, who they are seeing, and when',
      body:
        'The host is the member they are visiting, and that is the field people '
        + 'get lazy about. It is who gets notified on arrival and who is '
        + 'accountable for the guest, so it is worth getting right. The date and '
        + 'time window is when the pass works, and a guest who turns up outside '
        + 'it will be refused at the kiosk.',
      waitingLabel: 'Waiting for the invitation to be created.',
      advanceOn: [{ type: 'event', name: COACH_EVENT.visitorInvited }],
      allowSkip: true,
    },
    {
      id: 'the-pass',
      kind: 'narrate',
      route: VISITORS,
      target: CENTER,
      placement: 'center',
      title: 'One pass does both directions',
      body:
        'The guest gets a QR code. The same pass checks them in and, later, '
        + 'checks them out, and it is only spent on the way out. So do not treat '
        + 'a scanned pass as finished business. Someone still in the building '
        + 'with an unspent pass is exactly right.',
    },
    {
      id: 'walk-ins',
      kind: 'narrate',
      route: VISITORS,
      target: CENTER,
      placement: 'center',
      title: 'And the ones who just appear',
      body:
        'Add them on the Visitors tab as they stand there: name, phone, and who '
        + 'they are here to see. It takes twenty seconds and it means the '
        + 'building has an honest record of who was inside it today, which is '
        + 'the only reason any of this exists.',
    },
  ],
};

export const FRONTDESK_MEETING_ROOMS = {
  id: 'frontdesk-meeting-rooms',
  title: 'Run the meeting rooms',
  summary:
    'Read the day\'s bookings, book a room for someone at the desk, and know '
    + 'what to do when two people want the same slot.',
  category: 'frontdesk',
  level: 'advanced',
  permission: ['meeting_rooms.view', 'meeting_rooms.manage'],
  featureSlug: null,
  estMinutes: 5,
  readinessKey: null,
  keywords: [
    'meeting room', 'booking', 'book a room', 'conference room', 'calendar',
    'credits', 'reschedule', 'cancel booking',
  ],
  steps: [
    {
      id: 'intro',
      kind: 'narrate',
      route: MEETING_ROOMS,
      target: CENTER,
      placement: 'center',
      title: 'Two tabs, two different jobs',
      body:
        'Rooms is the inventory: what you have, what it seats, what it costs. '
        + 'Bookings is the day: who has what, and when. As a host you will spend '
        + 'nearly all your time on the second one.',
    },
    {
      id: 'bookings-tab',
      kind: 'act',
      route: MEETING_ROOMS,
      target: ANCHOR.meetingRoomBookingsTab,
      placement: 'bottom',
      title: 'Open the bookings',
      body:
        'Every reservation across every room, with the member who made it and '
        + 'what they were charged.',
      waitingLabel: 'Click the Bookings tab.',
      advanceOn: [{ type: 'click', target: ANCHOR.meetingRoomBookingsTab }],
      doItForMe: { type: 'click', target: ANCHOR.meetingRoomBookingsTab },
      allowSkip: true,
    },
    {
      id: 'statuses',
      kind: 'narrate',
      route: MEETING_ROOMS,
      target: CENTER,
      placement: 'center',
      title: 'A booking that is not paid for is not really a booking',
      body:
        'When a member books a paid room, the slot is held for a short window '
        + 'while they pay. If the payment never lands, the hold expires and the '
        + 'room goes back on sale. So a slot that looks taken this second may be '
        + 'free in a few minutes, and that is deliberate rather than a glitch.',
    },
    {
      id: 'calendar',
      kind: 'narrate',
      route: MEETING_ROOMS,
      target: CENTER,
      placement: 'center',
      title: 'Booking one for somebody at the desk',
      body:
        'Open a room to see its week as a grid, then click the empty slot you '
        + 'want and pick the member. Whether it costs them anything depends on '
        + 'their plan: most memberships bundle a monthly credit allowance, and '
        + 'the booking only turns into a charge once those credits run out.',
    },
    {
      id: 'self-serve',
      kind: 'narrate',
      route: MEETING_ROOMS,
      target: ANCHOR.meetingRoomPublicLink,
      placement: 'bottom',
      optionalTarget: true,
      title: 'The best booking is the one you never touch',
      body:
        'Members book rooms themselves from the app and the portal, and this '
        + 'public link lets anyone see availability without an account at all. '
        + 'If your desk is drowning in booking requests, sending people here is '
        + 'usually the fix.',
    },
    {
      id: 'clashes',
      kind: 'narrate',
      route: MEETING_ROOMS,
      target: CENTER,
      placement: 'center',
      title: 'When two people want the same room',
      body:
        'The system will not let you double-book, so the clash is always a '
        + 'human conversation rather than a system one. Reschedule or cancel '
        + 'from the booking itself instead of creating a second overlapping one. '
        + 'Cancelling returns the credits or triggers the refund, and doing it '
        + 'any other way leaves the member out of pocket.',
    },
  ],
};

export const FRONTDESK_SERVICE_REQUESTS = {
  id: 'frontdesk-service-requests',
  title: 'Log and chase service requests',
  summary:
    'Turn "the AC in room 4 is broken" into a ticket that actually reaches the '
    + 'person who fixes it.',
  category: 'frontdesk',
  level: 'advanced',
  permission: 'service_requests.manage',
  featureSlug: null,
  estMinutes: 4,
  readinessKey: null,
  keywords: [
    'service request', 'ticket', 'complaint', 'maintenance', 'helpdesk',
    'raise a ticket', 'issue', 'broken',
  ],
  steps: [
    {
      id: 'intro',
      kind: 'narrate',
      route: SR_HOME,
      target: CENTER,
      placement: 'center',
      title: 'Everything that is wrong with the building lives here',
      body:
        'Members raise these from the app, and you raise them for whoever '
        + 'catches you in the corridor. Both end up in the same queue, which is '
        + 'the entire benefit: nothing depends on you remembering it.',
    },
    {
      id: 'create',
      kind: 'act',
      route: SR_HOME,
      target: ANCHOR.serviceRequestNew,
      placement: 'bottom',
      title: 'Raise one now',
      body:
        'Log it while the person is still standing in front of you. A ticket '
        + 'written an hour later is missing the half of the detail that made it '
        + 'fixable.',
      waitingLabel: 'Click Create Ticket.',
      advanceOn: [
        { type: 'click', target: ANCHOR.serviceRequestNew },
        { type: 'route', path: SR_CREATE },
      ],
      doItForMe: { type: 'click', target: ANCHOR.serviceRequestNew },
      allowSkip: true,
    },
    {
      id: 'category',
      kind: 'await',
      route: SR_CREATE,
      target: CENTER,
      placement: 'center',
      title: 'The category is the routing, not a label',
      body:
        'Each category belongs to a team, and picking it is what decides who '
        + 'gets pinged. Get it wrong and the ticket sits with people who cannot '
        + 'act on it. Say where it is and what is actually happening, because '
        + '"AC not working" and "AC leaking onto a desk in room 4" are not the '
        + 'same job. Priority is about impact on the member, not how annoyed '
        + 'they sounded.',
      waitingLabel: 'Waiting for the ticket to be created.',
      advanceOn: [{ type: 'event', name: COACH_EVENT.serviceRequestCreated }],
      allowSkip: true,
    },
    {
      id: 'queue',
      kind: 'narrate',
      route: SR_TICKETS,
      target: CENTER,
      placement: 'center',
      title: 'And this is the queue you work',
      body:
        'Open it, assign it, comment as things move, close it when it is done. '
        + 'The member sees the status change on their side, so a ticket you '
        + 'quietly fixed but never closed still reads to them as ignored. '
        + 'Closing it is part of the fix.',
    },
  ],
};

export default [
  FRONTDESK_CHECKIN,
  FRONTDESK_VISITORS,
  FRONTDESK_MEETING_ROOMS,
  FRONTDESK_SERVICE_REQUESTS,
];
